import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import QRCode from 'qrcode'
import franchiseService from '../services/franchiseService'
import { formatDateTime } from '../utils/dateFormatter'
import './FranchiseDashboard.css'

const FranchiseTables = () => {
    const navigate = useNavigate()
    const [tables, setTables] = useState([])
    const [loading, setLoading] = useState(true)
    const [showAddModal, setShowAddModal] = useState(false)
    const [newTableNum, setNewTableNum] = useState('')
    const [qrCodes, setQrCodes] = useState({}) // Store generated QR data URLs
    const [selectedTable, setSelectedTable] = useState(null)
    const [tableOrders, setTableOrders] = useState([]) // All unpaid orders
    const [totalBillAmount, setTotalBillAmount] = useState(0)
    const [tableOrdersHistory, setTableOrdersHistory] = useState([])
    const [showBillModal, setShowBillModal] = useState(false)
    const [showHistoryModal, setShowHistoryModal] = useState(false)
    const [loadingBill, setLoadingBill] = useState(false)
    const [loadingHistory, setLoadingHistory] = useState(false)
    const [markingPaid, setMarkingPaid] = useState(false)
    const [showQRModal, setShowQRModal] = useState(false)
    const [selectedQRTable, setSelectedQRTable] = useState(null)

    useEffect(() => {
        fetchTables()
    }, [])

    const fetchTables = async () => {
        setLoading(true)
        try {
            const response = await franchiseService.getTables()
            if (response.success) {
                setTables(response.data)
                // Generate detailed QRs for display
                response.data.forEach(table => {
                    generateQR(table.qr_code, table.id)
                })
            }
        } catch (error) {
            console.error("Error fetching tables:", error)
        } finally {
            setLoading(false)
        }
    }

    const generateQR = async (payload, id) => {
        try {
            // Generate QR code with the full URL that customers will scan
            // The payload is the qr_code from the table, which will be used in /menu/table/:qrCode
            const baseURL = window.location.origin
            const qrURL = `${baseURL}/menu/table/${payload}`
            const url = await QRCode.toDataURL(qrURL)
            setQrCodes(prev => ({ ...prev, [id]: url }))
        } catch (err) {
            console.error(err)
        }
    }

    const handleAddTable = async (e) => {
        e.preventDefault()
        try {
            const response = await franchiseService.addTable({ tableNumber: newTableNum })
            if (response.success) {
                fetchTables() // Refresh list
                setShowAddModal(false)
                setNewTableNum('')
            }
        } catch (error) {
            alert(error.message || "Failed to add table")
        }
    }

    const handleDeleteTable = async (tableId) => {
        if (!window.confirm('Are you sure you want to delete this table? This will also delete the associated QR code. This action cannot be undone.')) {
            return
        }

        try {
            const response = await franchiseService.deleteTable(tableId)
            if (response.success) {
                fetchTables() // Refresh list
            }
        } catch (error) {
            alert(error.message || "Failed to delete table")
        }
    }

    const handleGetBill = async (table) => {
        setSelectedTable(table)
        setLoadingBill(true)
        setShowBillModal(true)
        try {
            const response = await franchiseService.getOrderByTable(table.id)
            console.log('Bill response:', response)
            if (response.success) {
                // Response now returns array of orders and totalAmount
                const orders = Array.isArray(response.data) ? response.data : (response.data ? [response.data] : [])
                setTableOrders(orders)
                setTotalBillAmount(response.totalAmount || orders.reduce((sum, order) => sum + parseFloat(order.amount || 0), 0))
                console.log(`Found ${orders.length} unpaid orders, total: ${response.totalAmount || totalBillAmount}`)
            } else {
                setTableOrders([])
                setTotalBillAmount(0)
            }
        } catch (error) {
            console.error('Error fetching bill:', error)
            setTableOrders([])
            setTotalBillAmount(0)
        } finally {
            setLoadingBill(false)
        }
    }

    const handleMarkAsPaid = async () => {
        if (!tableOrders || tableOrders.length === 0) return

        const orderCount = tableOrders.length
        if (!window.confirm(`Mark all ${orderCount} order(s) as paid and free up the table for the next customer?`)) {
            return
        }

        setMarkingPaid(true)
        try {
            // Mark all unpaid orders as delivered
            const updatePromises = tableOrders.map(order =>
                franchiseService.updateOrderStatus(order.id, 'delivered')
            )

            const results = await Promise.all(updatePromises)
            const allSuccess = results.every(r => r.success)

            if (allSuccess) {
                alert(`All ${orderCount} order(s) marked as paid. Table is now free for the next customer.`)
                setShowBillModal(false)
                setTableOrders([])
                setTotalBillAmount(0)
                setSelectedTable(null)
                fetchTables() // Refresh tables to update status
                // Refresh history if modal is open
                if (showHistoryModal && selectedTable) {
                    fetchTableHistory(selectedTable.id)
                }
            } else {
                alert('Some orders failed to update. Please try again.')
            }
        } catch (error) {
            alert(error.message || 'Failed to mark orders as paid')
        } finally {
            setMarkingPaid(false)
        }
    }

    const handleViewHistory = async (table) => {
        setSelectedTable(table)
        setLoadingHistory(true)
        setShowHistoryModal(true)
        try {
            const response = await franchiseService.getAllOrdersByTable(table.id)
            if (response.success) {
                setTableOrdersHistory(response.data || [])
            } else {
                setTableOrdersHistory([])
            }
        } catch (error) {
            console.error('Error fetching order history:', error)
            setTableOrdersHistory([])
        } finally {
            setLoadingHistory(false)
        }
    }

    const fetchTableHistory = async (tableId) => {
        try {
            const response = await franchiseService.getAllOrdersByTable(tableId)
            if (response.success) {
                setTableOrdersHistory(response.data || [])
            }
        } catch (error) {
            console.error('Error fetching order history:', error)
        }
    }

    return (
        <div className="franchise-dashboard-container">
            <div className="page-header">
                <div className="page-header-left">
                    <button
                        className="btn-secondary"
                        onClick={() => navigate('/franchise/dashboard')}
                    >
                        Back
                    </button>
                    <h1>Manage Tables</h1>
                </div>
                <button className="btn-primary" onClick={() => setShowAddModal(true)}>+ Add Table</button>
            </div>

            {loading ? (
                <div className="loading-state"><div className="spinner"></div><p>Loading tables...</p></div>
            ) : tables.length === 0 ? (
                <div className="empty-state">
                    <p>No tables found. Add your first table.</p>
                </div>
            ) : (
                <div className="tables-grid">
                    {tables.map(table => (
                        <div key={table.id} className={`table-card ${table.status ? table.status.toLowerCase() : 'available'}`}>
                            <div className="table-header">
                                <span className="table-number">Table {table.table_number}</span>
                                <span className={`status-badge ${table.status ? table.status.toLowerCase() : 'available'}`}>
                                    {table.status || 'AVAILABLE'}
                                </span>
                            </div>

                            <div className="table-content">
                                <div style={{ textAlign: 'center', padding: '1rem' }}>
                                    <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>Table</div>
                                    <p style={{ color: '#9ca3af', fontSize: '0.9rem' }}>
                                        {table.status === 'OCCUPIED' ? 'Currently Occupied' : 'Available'}
                                    </p>
                                </div>
                            </div>

                            <div className="table-footer">
                                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', width: '100%' }}>
                                    {table.status === 'OCCUPIED' && (
                                        <button
                                            className="btn-primary"
                                            onClick={() => handleGetBill(table)}
                                            style={{ flex: 1, minWidth: '120px' }}
                                        >
                                            Get Bill
                                        </button>
                                    )}
                                    <button
                                        className="btn-secondary"
                                        onClick={() => handleViewHistory(table)}
                                        style={{ flex: 1, minWidth: '120px' }}
                                    >
                                        Orders
                                    </button>
                                    <button
                                        className="btn-secondary"
                                        onClick={() => {
                                            setSelectedQRTable(table)
                                            setShowQRModal(true)
                                        }}
                                        style={{ flex: 1, minWidth: '120px' }}
                                    >
                                        Show QR
                                    </button>
                                    <button
                                        className="btn-icon delete"
                                        title="Delete Table"
                                        onClick={() => handleDeleteTable(table.id)}
                                        style={{ color: '#ef4444', padding: '0.5rem' }}
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showAddModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2>Add New Table</h2>
                            <button className="modal-close" onClick={() => setShowAddModal(false)}>×</button>
                        </div>
                        <form onSubmit={handleAddTable}>
                            <div className="form-group">
                                <label>Table Number</label>
                                <input
                                    placeholder="e.g. 5, 12, A1"
                                    value={newTableNum}
                                    onChange={e => setNewTableNum(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="modal-actions">
                                <button type="button" className="btn-secondary" onClick={() => setShowAddModal(false)}>Cancel</button>
                                <button type="submit" className="btn-primary">Create Table</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Bill Modal */}
            {showBillModal && (
                <div className="modal-overlay" onClick={() => setShowBillModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
                        <div className="modal-header">
                            <h2>Bill - Table {selectedTable?.table_number || 'N/A'}</h2>
                            <button className="modal-close" onClick={() => setShowBillModal(false)}>×</button>
                        </div>
                        <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                            {loadingBill ? (
                                <div style={{ textAlign: 'center', padding: '2rem' }}>
                                    <div className="spinner"></div>
                                    <p>Loading bill...</p>
                                </div>
                            ) : !tableOrders || tableOrders.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '2rem' }}>
                                    <p style={{ fontSize: '1.2rem', color: '#aaa' }}>No unpaid orders for this table</p>
                                </div>
                            ) : (
                                <>
                                    <div style={{ marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                                        <p style={{ margin: '0.5rem 0', fontSize: '1.1rem' }}>
                                            <strong>{tableOrders.length} Order{tableOrders.length > 1 ? 's' : ''}</strong> - All Unpaid
                                        </p>
                                    </div>

                                    {/* Show all orders grouped together */}
                                    <div style={{ marginBottom: '1.5rem' }}>
                                        <h3 style={{ marginBottom: '1rem', fontSize: '1.3rem', fontWeight: '600' }}>All Items from All Orders</h3>

                                        {tableOrders.map((order, orderIdx) => (
                                            <div key={order.id} style={{ marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: orderIdx < tableOrders.length - 1 ? '2px solid rgba(255,255,255,0.2)' : 'none' }}>
                                                <div style={{ marginBottom: '0.75rem', padding: '0.5rem', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '0.5rem' }}>
                                                    <p style={{ margin: '0.25rem 0', fontSize: '0.9rem', color: '#9ca3af' }}>
                                                        Order #{order.id} • <span className={`status-badge ${order.status}`} style={{ fontSize: '0.75rem' }}>{order.status}</span>
                                                    </p>
                                                </div>

                                                {order.items && Array.isArray(order.items) && order.items.length > 0 ? (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                                        {order.items.map((item, itemIdx) => (
                                                            <div
                                                                key={`${order.id}-${itemIdx}`}
                                                                style={{
                                                                    display: 'flex',
                                                                    justifyContent: 'space-between',
                                                                    alignItems: 'center',
                                                                    padding: '0.75rem',
                                                                    background: 'rgba(129, 140, 248, 0.1)',
                                                                    borderRadius: '0.5rem',
                                                                    border: '1px solid rgba(129, 140, 248, 0.2)'
                                                                }}
                                                            >
                                                                <div style={{ flex: 1 }}>
                                                                    <p style={{ fontWeight: '600', marginBottom: '0.25rem', fontSize: '0.95rem' }}>
                                                                        {item.name || `Item ${itemIdx + 1}`}
                                                                    </p>
                                                                    <p style={{ fontSize: '0.85rem', color: '#9ca3af' }}>
                                                                        ₹{parseFloat(item.price || 0).toFixed(2)} × {item.quantity || 1}
                                                                    </p>
                                                                </div>
                                                                <p style={{ fontWeight: '700', fontSize: '1.1rem', color: '#fff', minWidth: '80px', textAlign: 'right' }}>
                                                                    ₹{((parseFloat(item.price || 0)) * (item.quantity || 1)).toFixed(2)}
                                                                </p>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <p style={{ color: '#aaa', fontSize: '0.9rem', fontStyle: 'italic' }}>No items in this order</p>
                                                )}

                                                <div style={{ marginTop: '0.75rem', textAlign: 'right' }}>
                                                    <p style={{ fontSize: '0.9rem', color: '#9ca3af' }}>Order Total:</p>
                                                    <p style={{ fontWeight: '700', fontSize: '1.1rem', color: '#818cf8' }}>
                                                        ₹{parseFloat(order.amount || 0).toFixed(2)}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <div style={{
                                        padding: '1.5rem',
                                        background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.2), rgba(16, 185, 129, 0.15))',
                                        borderRadius: '0.75rem',
                                        marginTop: '1.5rem',
                                        border: '2px solid #22c55e',
                                        boxShadow: '0 4px 12px rgba(34, 197, 94, 0.2)'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div>
                                                <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '700' }}>Final Total</h3>
                                                <p style={{ margin: '0.5rem 0 0', fontSize: '0.9rem', color: '#9ca3af' }}>
                                                    {tableOrders.length} order{tableOrders.length > 1 ? 's' : ''} combined
                                                </p>
                                            </div>
                                            <h3 style={{ margin: 0, fontSize: '2.5rem', color: '#22c55e', fontWeight: 'bold' }}>
                                                ₹{totalBillAmount.toFixed(2)}
                                            </h3>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                        {tableOrders && tableOrders.length > 0 && (
                            <div className="modal-actions">
                                <button
                                    className="btn-secondary"
                                    onClick={() => setShowBillModal(false)}
                                >
                                    Close
                                </button>
                                <button
                                    className="btn-primary"
                                    onClick={handleMarkAsPaid}
                                    disabled={markingPaid}
                                >
                                    {markingPaid ? 'Processing...' : `Mark All ${tableOrders.length} Order${tableOrders.length > 1 ? 's' : ''} as Paid`}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* QR Code Modal */}
            {showQRModal && selectedQRTable && (
                <div className="modal-overlay" onClick={() => setShowQRModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px', textAlign: 'center' }}>
                        <div className="modal-header">
                            <h2>QR Code - Table {selectedQRTable.table_number}</h2>
                            <button className="modal-close" onClick={() => setShowQRModal(false)}>×</button>
                        </div>
                        <div className="modal-body" style={{ padding: '2rem' }}>
                            {qrCodes[selectedQRTable.id] ? (
                                <>
                                    <div style={{
                                        background: 'var(--bg-card)',
                                        padding: '1rem',
                                        borderRadius: '0.5rem',
                                        display: 'inline-block',
                                        marginBottom: '1rem'
                                    }}>
                                        <img
                                            src={qrCodes[selectedQRTable.id]}
                                            alt={`QR for Table ${selectedQRTable.table_number}`}
                                            style={{ width: '250px', height: '250px' }}
                                        />
                                    </div>
                                    <p style={{ color: '#9ca3af', fontSize: '0.9rem', marginTop: '1rem' }}>
                                        Scan this QR code to view menu and place order
                                    </p>
                                    <button
                                        className="btn-primary"
                                        onClick={() => {
                                            const link = document.createElement('a')
                                            link.href = qrCodes[selectedQRTable.id]
                                            link.download = `table-${selectedQRTable.table_number}-qr.png`
                                            link.click()
                                        }}
                                        style={{ marginTop: '1rem' }}
                                    >
                                        Download QR
                                    </button>
                                </>
                            ) : (
                                <div className="spinner"></div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Order History Modal */}
            {showHistoryModal && selectedTable && (
                <div className="modal-overlay" onClick={() => setShowHistoryModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', maxHeight: '90vh' }}>
                        <div className="modal-header">
                            <h2>Order History - Table {selectedTable.table_number}</h2>
                            <button className="modal-close" onClick={() => setShowHistoryModal(false)}>×</button>
                        </div>
                        <div className="modal-body" style={{ maxHeight: 'calc(90vh - 120px)', overflowY: 'auto' }}>
                            {loadingHistory ? (
                                <div style={{ textAlign: 'center', padding: '2rem' }}>
                                    <div className="spinner"></div>
                                    <p>Loading order history...</p>
                                </div>
                            ) : tableOrdersHistory.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '2rem' }}>
                                    <p style={{ fontSize: '1.2rem', color: '#aaa' }}>No orders found for this table</p>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                    {tableOrdersHistory.map((order, idx) => (
                                        <div
                                            key={order.id}
                                            style={{
                                                padding: '1rem',
                                                background: order.status === 'delivered'
                                                    ? 'rgba(34, 197, 94, 0.1)'
                                                    : 'rgba(129, 140, 248, 0.1)',
                                                borderRadius: '0.5rem',
                                                border: `1px solid ${order.status === 'delivered' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(129, 140, 248, 0.3)'}`
                                            }}
                                        >
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.75rem' }}>
                                                <div>
                                                    <p style={{ fontWeight: '600', fontSize: '1.1rem', marginBottom: '0.25rem' }}>
                                                        Order #{order.id}
                                                    </p>
                                                    <p style={{ fontSize: '0.85rem', color: '#9ca3af' }}>
                                                        {formatDateTime(order.created_at)}
                                                    </p>
                                                </div>
                                                <div style={{ textAlign: 'right' }}>
                                                    <span className={`status-badge ${order.status}`} style={{ marginBottom: '0.25rem', display: 'inline-block' }}>
                                                        {order.status}
                                                    </span>
                                                    <p style={{ fontWeight: '700', fontSize: '1.2rem', color: '#22c55e', marginTop: '0.25rem' }}>
                                                        ₹{parseFloat(order.amount).toFixed(2)}
                                                    </p>
                                                </div>
                                            </div>

                                            {order.items && order.items.length > 0 && (
                                                <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                                                    {order.items.map((item, itemIdx) => (
                                                        <div
                                                            key={itemIdx}
                                                            style={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                marginBottom: '0.5rem',
                                                                fontSize: '0.9rem'
                                                            }}
                                                        >
                                                            <span style={{ color: '#e5e7eb' }}>
                                                                {item.name} × {item.quantity}
                                                            </span>
                                                            <span style={{ fontWeight: '600', color: '#fff' }}>
                                                                ₹{(parseFloat(item.price) * item.quantity).toFixed(2)}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="modal-actions">
                            <button
                                className="btn-secondary"
                                onClick={() => setShowHistoryModal(false)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default FranchiseTables
